import axios from "axios";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

// ─── Global 401 interceptor ───────────────────────────────────────────────
// Any 401 response (except the login endpoint itself) means the session has
// expired or the token is invalid. Redirect to /login so the user can
// re-authenticate instead of leaving them in a silent broken state.
const AUTH_SKIP = ["/auth/login", "/auth/captcha", "/auth/resend-2fa"];

// Public frontend paths where an unauthenticated /auth/me 401 is expected —
// do NOT redirect away from these pages on a 401.
const PUBLIC_PATHS = ["/login", "/reset-password", "/forgot-password"];

// Guard: when multiple parallel requests all 401 at the same time, only the
// first one should trigger the redirect — the rest are no-ops.
let _redirecting = false;

api.interceptors.response.use(
  (res) => res,
  (error) => {
    // Flatten FastAPI 422 detail arrays to a string so raw `.detail` renders never crash React
    const det = error?.response?.data?.detail;
    if (Array.isArray(det)) {
      error.response.data.detail = det.map((e) => (e && e.msg) || JSON.stringify(e)).join("; ");
    }
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    const isAuthEndpoint = AUTH_SKIP.some((s) => url.includes(s));
    const isPublicPath = PUBLIC_PATHS.some((p) => window.location.pathname.startsWith(p));

    if (status === 401 && !isAuthEndpoint && !isPublicPath && !_redirecting) {
      _redirecting = true;
      sessionStorage.setItem("session_expired", "1");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export function fmtErr(detail) {
  // Accept a full Axios error object — extract the meaningful part automatically
  if (detail && typeof detail === "object" && detail.isAxiosError !== undefined) {
    if (!detail.response) return "Network error. Please check your connection and try again.";
    const d = detail.response?.data?.detail;
    if (d) return fmtErr(d);
    const status = detail.response?.status;
    if (status === 400) return "Invalid request. Please check your file or inputs and try again.";
    if (status === 403) return "You don't have permission to perform this action.";
    if (status === 413) return "File is too large to upload.";
    if (status === 422) return "Validation error. Please check your inputs.";
    if (status === 429) return "Too many requests. Please wait and try again.";
    if (status >= 500) return "Server error. Please try again in a moment.";
    return `Request failed (${status}). Please try again.`;
  }
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg) || JSON.stringify(e)).join("; ");
  if (typeof detail.msg === "string") return detail.msg;
  if (typeof detail.message === "string") return detail.message;
  return String(detail);
}

export default api;
